<template>
    <div class="yt-video-wrapper">
        <loading :height="80" :width="80" color="var(--wecodefy-secondary-color, #9fc953)" :opacity="1"
            :active.sync="isInitialLoading" :is-full-page="false" :can-cancel="false" background-color="#000"></loading>
        <div id="video-container">
            <div class="shaka-container" id="shaka-container" data-shaka-player-container>
                <video class="videoel" id="videoel" data-shaka-player autoplay></video>
            </div>
        </div>
    </div>
</template>
  
<script>
import { Innertube, UniversalCache } from 'youtubei.js/bundle/browser';
import shaka from 'shaka-player/dist/shaka-player.ui.js';
import "shaka-player/dist/controls.css";
import { PROXY_SERVER_HOST_URL, PROXY_SERVER_PROTOCOL_URL } from "@/store/variables.js"
export default {
    name: "YTVideoPlayer",
    props: {
        url: {
            type: String,
            required: true
        },
        proxServer: {
            type: String,
            default: PROXY_SERVER_HOST_URL
        },
        protocol: {
            type: String,
            default: PROXY_SERVER_PROTOCOL_URL
        }
    },
    data() {
        return {
            isInitialLoading: false,
            player: shaka.Player | undefined,
            yt: null,
            ui: shaka.ui.Overlay | undefined
        };
    },
    computed: {
        videoIdOrURL() {
            let vm = this;
            return vm.url;
        }
    },
    mounted() {
        let vm = this;
        vm.initiatePlayer();
    },
    async beforeDestroy() {
        let vm = this;
        if (vm.player) {
            await vm.player.destroy();
            vm.player = undefined;
        }

        if (vm.ui) {
            vm.ui.destroy();
            vm.ui = undefined;
        }
    },
    watch: {
        url() {
            let vm = this;
            vm.initiatePlayer();
        }
    },
    methods: {
        async initiatePlayer() {
            let vm = this;
            vm.isInitialLoading = true;
            vm.yt = await Innertube.create({
                generate_session_locally: true,
                fetch: async (input, init) => {
                    const url = typeof input === 'string'
                        ? new URL(input)
                        : input instanceof URL
                            ? input
                            : new URL(input.url);

                    // Transform the url for use with our proxy.
                    url.searchParams.set('__host', url.host);
                    url.host = vm.proxServer;
                    url.protocol = vm.protocol;

                    const headers = init && init.headers
                        ? new Headers(init.headers)
                        : input instanceof Request
                            ? input.headers
                            : new Headers();

                    // Now serialize the headers.
                    url.searchParams.set('__headers', JSON.stringify([...headers]));

                    if (input instanceof Request) {
                        // @ts-ignore
                        input.duplex = 'half';
                    }

                    // Copy over the request.
                    const request = new Request(
                        url,
                        input instanceof Request ? input : undefined,
                    );

                    headers.delete('user-agent');

                    return fetch(request, init ? {
                        ...init,
                        headers
                    } : {
                        headers
                    });
                },
                cache: new UniversalCache(false),
            });
            let videoId;
            try {
                if (vm.videoIdOrURL.match(/(http|https):\/\/([\w_-]+(?:(?:\.[\w_-]+)+))([\w.,@?^=%&:\/~+#-]*[\w@?^=%&\/~+#-])/)) {
                    const endpoint = await vm.yt.resolveURL(vm.videoIdOrURL);
                    if (!endpoint.payload.videoId) {
                        console.log('Could not resolve URL');
                        // showUI({ hidePlayer: true });
                        return;
                    }

                    videoId = endpoint.payload.videoId;
                } else {
                    videoId = vm.videoIdOrURL;
                }

                const info = await vm.yt.getInfo(videoId);
                const dash = await info.toDash();
                const uri = info.streaming_data.hls_manifest_url ? info.streaming_data.hls_manifest_url : 'data:application/dash+xml;charset=utf-8;base64,' + btoa(dash);

                if (vm.player) {
                    await vm.player.destroy();
                    vm.player = undefined;
                }

                if (vm.ui) {
                    vm.ui.destroy();
                    vm.ui = undefined;
                }

                const videoEl = document.getElementById('videoel');
                const shakaContainer = document.getElementById('shaka-container');

                shakaContainer
                    .querySelectorAll("div")
                    .forEach(node => node.remove());

                shaka.polyfill.installAll();

                if (shaka.Player.isBrowserSupported()) {
                    videoEl.poster = info.basic_info.thumbnail ? info.basic_info.thumbnail[0].url : '';
                    vm.isInitialLoading = false;

                    vm.player = new shaka.Player(videoEl);
                    vm.ui = new shaka.ui.Overlay(vm.player, shakaContainer, videoEl);

                    const config = {
                        seekBarColors: {
                            base: 'rgba(255,255,255,.2)',
                            buffered: 'rgba(255,255,255,.4)',
                            played: 'rgb(255,0,0)',
                        },
                        fadeDelay: 0,
                    };

                    vm.ui.configure(config);

                    const overflowMenuButton = document.querySelector('.shaka-overflow-menu-button');
                    if (overflowMenuButton) {
                        overflowMenuButton.innerHTML = 'settings';
                    }

                    const backToOverflowButton = document.querySelector('.shaka-back-to-overflow-button .material-icons-round');
                    if (backToOverflowButton) {
                        backToOverflowButton.innerHTML = 'arrow_back_ios_new';
                    }

                    vm.player.configure({
                        streaming: {
                            bufferingGoal: 180,
                            rebufferingGoal: 0.02,
                            bufferBehind: 300
                        }
                    });

                    vm.player.getNetworkingEngine().registerRequestFilter((_type, request) => {
                        const uri = request.uris[0];
                        const url = new URL(uri);
                        const headers = request.headers;

                        if (url.host.endsWith(".googlevideo.com") || headers.Range) {
                            url.searchParams.set('__host', url.host);
                            url.host = vm.proxServer;
                            url.protocol = vm.protocol;
                        }

                        request.method = 'POST';

                        // protobuf - { 15: 0 }
                        request.body = new Uint8Array([120, 0]);

                        if (url.pathname === "/videoplayback") {
                            if (headers.Range) {
                                request.headers = {};
                                url.searchParams.set("range", headers.Range.split("=")[1]);
                                url.searchParams.set("alr", "yes");
                            }
                        }

                        request.uris[0] = url.toString();
                    });

                    // The UTF-8 characters "h", "t", "t", and "p".
                    const HTTP_IN_HEX = 0x68747470;

                    const RequestType = shaka.net.NetworkingEngine.RequestType;

                    vm.player.getNetworkingEngine().registerResponseFilter(async (type, response) => {
                        const dataView = new DataView(response.data);

                        if (response.data.byteLength < 4 ||
                            dataView.getUint32(0) != HTTP_IN_HEX) {
                            // This doesn't start with "http", so it is not an ALR.
                            return;
                        }

                        // Interpret the response data as a URL string.
                        const response_as_string = shaka.util.StringUtils.fromUTF8(response.data);

                        let retry_parameters;

                        if (type == RequestType.MANIFEST) {
                            retry_parameters = vm.player.getConfiguration().manifest.retryParameters;
                        } else if (type == RequestType.SEGMENT) {
                            retry_parameters = vm.player.getConfiguration().streaming.retryParameters;
                        } else if (type == RequestType.LICENSE) {
                            retry_parameters = vm.player.getConfiguration().drm.retryParameters;
                        } else {
                            retry_parameters = shaka.net.NetworkingEngine.defaultRetryParameters();
                        }

                        // Make another request for the redirect URL.
                        const uris = [response_as_string];
                        const redirect_request = shaka.net.NetworkingEngine.makeRequest(uris, retry_parameters);
                        const request_operation = vm.player.getNetworkingEngine().request(type, redirect_request);
                        const redirect_response = await request_operation.promise;

                        // Modify the original response to contain the results of the redirect
                        // response.
                        response.data = redirect_response.data;
                        response.headers = redirect_response.headers;
                        response.uri = redirect_response.uri;
                    });

                    try {
                        await vm.player.load(uri);
                    } catch (e) {
                        console.error('Could not load manifest', e);
                    }
                } else {
                    console.error('Browser not supported!');
                }

            }
            catch (error) {
                vm.isInitialLoading = false;
                console.error(error);
            }
        },
    },
};
</script>

<style lang="scss">
.yt-video-wrapper {
    display: flex;
    height: 100%;
    width: 100%;
    align-items: center;
    position: relative;

    #video-container {
        height: 100%;
        width: 100%;
        display: flex;

        .shaka-container {
            height: 100%;
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: center;

            video {
                height: 100%;
                width: 100%;
            }
        }
    }
}
</style>